import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import FolioRule from "@/components/FolioRule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState({ submissions: [], archive: [], cellar: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/submissions`, { headers }),
      axios.get(`${API}/admin/archive`, { headers }),
      axios.get(`${API}/admin/cellar`, { headers }),
    ])
      .then(([s, a, c]) => setData({ submissions: s.data, archive: a.data, cellar: c.data }))
      .catch(() => setData({ submissions: [], archive: [], cellar: [] }))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div data-testid="admin-dashboard">
      <FolioRule label="00 / OVERVIEW" className="mb-12" />
      <h1 className="font-display text-5xl md:text-6xl leading-tight text-cream">{t("admin.welcome")}</h1>

      <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-10 max-w-5xl">
        <Stat
          label={t("admin.total_submissions")}
          value={loading ? "—" : data.submissions.length}
          testid="stat-submissions"
        />
        <Stat
          label={t("admin.total_archive")}
          value={loading ? "—" : data.archive.length}
          testid="stat-archive"
        />
        <Stat
          label={t("admin.total_cellar")}
          value={loading ? "—" : data.cellar.length}
          testid="stat-cellar"
        />
      </div>

      <div className="mt-20">
        <div className="font-mono text-[10px] tracking-widest2 uppercase text-gold/80 mb-6">{t("admin.latest")}</div>
        <div className="border border-gold/20">
          {data.submissions.slice(0, 5).map((s, i) => (
            <div
              key={s.id}
              className={`grid md:grid-cols-12 gap-4 px-6 py-5 ${i > 0 ? "border-t border-gold/15" : ""}`}
              data-testid={`recent-${s.id}`}
            >
              <div className="md:col-span-3 font-display italic text-cream text-lg">{s.name}</div>
              <div className="md:col-span-3 text-cream/70 text-sm">{s.email}</div>
              <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-gold/80">
                {s.path === "raw_material" ? "Raw Material" : "Private Label"}
              </div>
              <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
                {new Date(s.created_at).toLocaleString()}
              </div>
            </div>
          ))}
          {data.submissions.length === 0 && !loading ? (
            <div className="px-6 py-8 font-mono text-[10px] tracking-widest2 uppercase text-cream/45">
              {t("admin.no_submissions")}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-gold/30 p-8" data-testid={testid}>
      <div className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55">{label}</div>
      <div className="mt-4 font-display text-7xl text-gold/95 italic leading-none">{value}</div>
    </div>
  );
}
